import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  TaskRunBriefDetail,
  TaskRunBriefEvidence,
  TaskRunBriefSectionEvidence,
  TaskFindingStatus,
} from "../../../../shared/types";
import { Badge } from "../ui/Badge";
import { Markdown } from "../agent/Markdown";
import { WorkspaceHeader } from "../layout/WorkspaceHeader";
import { CorrectionButton } from "../feedback/CorrectionButton";
import { RecommendationEvidenceDrawer } from "./RecommendationEvidenceDrawer";
import { BriefSectionEvidenceMap } from "./BriefSectionEvidenceMap";
import { annotateBriefWithEvidence } from "./briefEvidence";
import { useSetFindingStatus } from "../../hooks/useTaskFinding";
import { trustGateActionCopy, trustGateLabel, trustGateReasonLabels, trustGateSummary } from "../../lib/trust-state";
import { workObjectDisplaySpec, type WorkObjectType, type WorkObjectDisplayState } from "../../lib/work-object-display";
import {
  classifyFindingTrust,
  relevanceLabel,
  summarizeBriefTrust,
  type FindingTrustKind,
} from "../../lib/finding-trust";

type TabKey = "brief" | "findings" | "details";

const TRUST_BADGE_VARIANT: Record<FindingTrustKind, "success" | "warning" | "default"> = {
  verified: "success",
  unverified: "warning",
  internal: "default",
};

function humaniseDocumentType(value: string | null | undefined): string {
  return value?.trim()
    ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "Unknown";
}

function readinessVariant(readiness: string): "success" | "warning" | "error" | "default" {
  switch (readiness) {
    case "usable": return "success";
    case "failed": return "error";
    case "running": return "default";
    default: return "warning";
  }
}

/**
 * PRD-0084 — operator-readable brief / output report.
 *
 * Renders one brief document as a tabbed report (Brief · Findings & Evidence ·
 * Details). Content leads; engineering metadata is demoted into Details; trust
 * signals are translated to plain language; findings carry a trust badge + a
 * relevance label and can be triaged (Done / Dismiss). System events are tagged
 * as such, never "source unavailable".
 */
export function OperatorBriefReport({
  brief,
  statusLabel,
  readiness,
  onBack,
  objectType,
  surfaceLabel,
  primaryTabLabel,
  openOutputLabel,
}: {
  brief: TaskRunBriefDetail;
  statusLabel?: string;
  /** Task-run readiness (usable / usable_with_warnings / failed …) — surfaced when
   *  it diverges from the run status so a "completed" run can't hide a failed output. */
  readiness?: string | null;
  onBack?: () => void;
  objectType?: WorkObjectType;
  surfaceLabel?: string;
  primaryTabLabel?: string;
  openOutputLabel?: string;
}) {
  const [tab, setTab] = useState<TabKey>("brief");
  // Local triage overrides for immediate feedback; persisted via the mutation.
  const [triaged, setTriaged] = useState<Record<string, TaskFindingStatus>>({});
  const setFindingStatus = useSetFindingStatus(brief.run_id);

  const supportingFindings = brief.supporting_findings ?? [];
  const sectionEvidence = brief.section_evidence ?? [];
  const trustGate = brief.trust_gate ?? null;
  // The trust gate (PRD-0075) is the strongest signal — when it blocks/degrades a
  // brief, surface its guidance prominently; otherwise fall back to the per-finding
  // source-verification summary.
  const gateState = trustGate?.state;
  const gateBlocking = gateState === "blocked" || gateState === "degraded";
  const trust = useMemo(() => summarizeBriefTrust(supportingFindings), [supportingFindings]);
  const annotated = useMemo(
    () => annotateBriefWithEvidence(brief.content, supportingFindings, sectionEvidence),
    [brief.content, supportingFindings, sectionEvidence],
  );

  // Group findings by section for the Findings tab; fall back to a single group.
  const sections: TaskRunBriefSectionEvidence[] = sectionEvidence.length > 0
    ? sectionEvidence
    : supportingFindings.length > 0
      ? [{ section_key: "metrics", label: "Findings", findings: supportingFindings }]
      : [];

  const createdAt = brief.created_at ? new Date(brief.created_at).toLocaleString() : "Unknown";

  // Optimistic triage with rollback. The effective state is the local override if
  // present, otherwise the server-provided finding.status — so Done/Dismiss survives
  // a reload once the brief refetches.
  function effectiveStatus(finding: TaskRunBriefEvidence): TaskFindingStatus {
    return triaged[finding.finding_id] ?? finding.status ?? "new";
  }
  function triage(finding: TaskRunBriefEvidence, status: TaskFindingStatus) {
    const previous = triaged[finding.finding_id];
    setTriaged((prev) => ({ ...prev, [finding.finding_id]: status }));
    setFindingStatus.mutate(
      { findingId: finding.finding_id, status },
      {
        onError: () =>
          setTriaged((prev) => {
            const next = { ...prev };
            if (previous === undefined) delete next[finding.finding_id];
            else next[finding.finding_id] = previous;
            return next;
          }),
      },
    );
  }

  const resolvedObjectType = objectType ?? (
    brief.document_type === "voice_review"
      ? "voice_review"
      : brief.document_type === "published"
        ? "published_handoff"
        : brief.document_type === "daily_brief"
          ? "daily_brief"
          : brief.document_type === "brief"
            ? "brief"
            : "draft"
  );
  const displayState: WorkObjectDisplayState = trustGate?.state === "blocked"
    ? "failed"
    : brief.document_type === "voice_review"
      ? "open"
      : "unknown";
  const displaySpec = workObjectDisplaySpec(resolvedObjectType, displayState);
  const kindLabel = surfaceLabel
    ?? (
      displaySpec.objectType === "voice_review"
        ? "Voice Review"
        : brief.kind === "daily_brief"
          ? "Daily Brief"
          : "Brief"
    );
  const leadingTabLabel = primaryTabLabel
    ?? (displaySpec.objectType === "voice_review" ? "Review" : "Brief");
  const canonicalOutputLabel = openOutputLabel
    ?? (displaySpec.objectType === "voice_review" ? "Open canonical review" : "Open canonical output");

  return (
    <div className="space-y-5">
      <WorkspaceHeader
        eyebrow={`Operator Workspace · ${kindLabel}`}
        title={brief.title}
        description={`${brief.findings_count} finding${brief.findings_count === 1 ? "" : "s"} · ${brief.run_date}`}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Review" },
          { label: kindLabel },
        ]}
        badges={(
          <>
            {/* When a trust gate blocks/degrades the brief, the badge label matches its
                colour (Blocked/Degraded) — never a red "completed". Otherwise it shows
                the run status, and trust lives in the banner + per-finding badges. */}
            {gateBlocking
              ? <Badge variant={gateState === "blocked" ? "error" : "warning"}>{gateState === "blocked" ? "Blocked" : "Degraded"}</Badge>
              : <Badge variant={trust.hasWarning ? "warning" : "success"}>{statusLabel ?? "unknown"}</Badge>}
            {readiness && readiness !== "usable" && (
              <Badge variant={readinessVariant(readiness)}>{readiness.replace(/_/g, " ")}</Badge>
            )}
          </>
        )}
        actions={onBack
          ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            >
              Back to workspace
            </button>
          )
          : undefined}
        metadata={(
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
            Primary action {displaySpec.primaryCta.label}
          </span>
        )}
      />

      {/* Trust banner (FR-5). Trust-gate blocking takes priority over the per-finding
          source-verification summary; both are plain-language, no provenance jargon. */}
      {gateBlocking && trustGate ? (
        <div className={`rounded-[16px] border p-4 ${gateState === "blocked" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
          <p className={`text-sm font-semibold ${gateState === "blocked" ? "text-red-900" : "text-amber-900"}`}>{trustGateLabel(trustGate)}</p>
          <p className={`mt-1 text-xs ${gateState === "blocked" ? "text-red-800" : "text-amber-800"}`}>{trustGateSummary(trustGate)}</p>
          {trustGateReasonLabels(trustGate).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {trustGateReasonLabels(trustGate).map((reason) => (
                <span key={reason} className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">{reason}</span>
              ))}
            </div>
          )}
          <p className={`mt-2 text-xs font-medium ${gateState === "blocked" ? "text-red-900" : "text-amber-900"}`}>{trustGateActionCopy(trustGate)}</p>
        </div>
      ) : trust.hasWarning ? (
        <div className="flex items-start gap-3 rounded-[16px] border border-amber-200 bg-amber-50 p-4">
          <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full bg-amber-500 text-xs font-bold text-white">!</span>
          <div>
            <p className="text-sm font-semibold text-amber-900">{trust.text}</p>
            <p className="mt-1 text-xs text-amber-800">
              We have the headline but not a working link or date for these. The {trust.verified} verified finding{trust.verified === 1 ? "" : "s"} and all system events are unaffected.{" "}
              <button type="button" onClick={() => setTab("details")} className="font-medium underline">See why →</button>
            </p>
          </div>
        </div>
      ) : null}

      {/* Inner segmented tabs (shipped inspector pattern) */}
      <div className="flex items-center justify-between gap-4 rounded-[16px] border border-slate-200 bg-white p-2 shadow-sm">
        <div role="tablist" aria-label="Brief report views" className="inline-flex gap-1 rounded-xl bg-slate-50 p-1">
          {([["brief", leadingTabLabel], ["findings", `Findings & Evidence`], ["details", "Details"]] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-sm font-semibold ${
                tab === key ? "bg-white text-brand-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
              {key === "findings" && supportingFindings.length > 0 && (
                <span className={`rounded-full px-1.5 text-xs ${tab === key ? "bg-brand-100 text-brand-700" : "bg-slate-200 text-slate-600"}`}>
                  {supportingFindings.length}
                </span>
              )}
            </button>
          ))}
        </div>
        {brief.output_route_path && (
          <Link
            to={brief.output_route_path}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            {canonicalOutputLabel}
          </Link>
        )}
      </div>

      {/* BRIEF — content leads */}
      {tab === "brief" && (
        <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
          <BriefSectionEvidenceMap content={brief.content} findings={supportingFindings} sectionEvidence={sectionEvidence} />
          <div className="mt-4">
            <Markdown content={annotated} />
          </div>
        </div>
      )}

      {/* FINDINGS & EVIDENCE — trust badges, relevance, triage; system events distinct */}
      {tab === "findings" && (
        <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
          {sections.length === 0 && <p className="text-sm text-slate-600">No findings backed this brief.</p>}
          {sections.map((section) => {
            const isActionItems = section.section_key === "action_items";
            return (
              <div key={section.section_key} className="mb-6 last:mb-0">
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">{section.label}</h3>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 text-xs text-slate-500">{section.findings.length}</span>
                </div>
                {isActionItems && (
                  <p className="mb-3 text-xs text-slate-500">From your pipeline — these are system events, not research, so they have no external source.</p>
                )}
                <div className="space-y-2.5">
                  {section.findings.map((finding) => {
                    const classification = classifyFindingTrust(finding);
                    const relevance = relevanceLabel(finding.score);
                    const state = effectiveStatus(finding);
                    if (state === "dismissed") {
                      return (
                        <div key={finding.finding_id} className="flex items-center justify-between rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-400">
                          <span className="line-through">{finding.title}</span>
                          <button type="button" className="text-xs font-semibold text-slate-500 hover:text-slate-700" onClick={() => triage(finding, "new")}>Undo</button>
                        </div>
                      );
                    }
                    return (
                      <div key={finding.finding_id} className={`flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3 ${classification.kind === "internal" ? "bg-slate-50" : "bg-white"}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900">{finding.title}</p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <Badge variant={TRUST_BADGE_VARIANT[classification.kind]}>
                              {classification.label}{classification.detail ? ` · ${classification.detail}` : ""}
                            </Badge>
                            {relevance && <span className="text-xs font-medium text-slate-500">relevance: {relevance}</span>}
                            {state === "acted" && <span className="text-xs font-semibold text-emerald-600">Done ✓</span>}
                          </div>
                        </div>
                        <div className="flex flex-none gap-1.5">
                          {state === "acted"
                            ? <button type="button" className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-50" onClick={() => triage(finding, "new")}>Undo</button>
                            : (
                              <>
                                <button type="button" className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => triage(finding, "acted")}>Done</button>
                                {!isActionItems && (
                                  <button type="button" className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50" onClick={() => triage(finding, "dismissed")}>Dismiss</button>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAILS — demoted metadata + raw provenance */}
      {tab === "details" && (
        <div className="space-y-4">
          <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Field label="Source" value={humaniseDocumentType(brief.document_type)} />
              <Field label="Created" value={createdAt} />
              <Field label="Run" value={brief.run_id} mono />
              <Field label="Document key" value={brief.document_key ?? "—"} mono />
            </dl>
            {brief.output_route_path && (
              <Link to={brief.output_route_path} className="mt-4 inline-flex text-sm font-medium text-brand-700 hover:underline">
                {canonicalOutputLabel}
              </Link>
            )}
          </div>

          {(brief.resolver_warnings?.length ?? 0) > 0 || trust.unverified > 0 ? (
            <div className="rounded-[16px] border border-slate-200 bg-slate-950 p-4 font-mono text-xs text-slate-300">
              <p className="mb-2 text-slate-400">Raw provenance (for debugging)</p>
              {trust.unverified > 0 && (
                <p>· {trust.unverified} finding(s) missing one of: source_url, domain, published_at — see PRD-0084 FR-10 (evidence-capture gap).</p>
              )}
              {(brief.resolver_warnings ?? []).map((w) => (
                <p key={`${w.reason}:${w.message}`}>· {w.reason}: {w.message}</p>
              ))}
            </div>
          ) : null}

          <div className="rounded-[20px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-800">Recommendation feedback</h2>
              <CorrectionButton
                brandId={brief.brand_id}
                sourceType="brief"
                sourceId={brief.brief_id ?? brief.run_id}
                contextHint={brief.content.slice(0, 240) || brief.title}
              />
            </div>
            <RecommendationEvidenceDrawer findings={supportingFindings} title="Supporting findings" />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</dt>
      <dd className={`mt-1 text-sm text-slate-900 ${mono ? "break-all font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
